import { observable, runInAction, action, computed } from 'mobx'

import * as Prismic from 'prismic-javascript'
import * as PrismicDOM from 'prismic-dom'
import * as _ from 'lodash'

import { CommonStore } from '../common/CommonStore'

type ThenArg<T> = T extends Promise<infer U> ? U : T
type PrismicAPIPromise = ReturnType<typeof Prismic.getApi>
type PrismicAPI = ThenArg<PrismicAPIPromise>

interface Fetchable {
  fetch (api: PrismicAPI, lang: string): Promise<Fetchable>

}
class ContentModel<T extends Fetchable> implements Fetchable {
  _ref: ContentRef<T>

  constructor (ref: ContentRef<T>) {
    this._ref = ref
    // console.log(ref.type)
  }

  async fetch (api: PrismicAPI, lang: string) {
    // fetch from prsmic by using this._ref.id and this._ref.type

    const response = await api.query([
      Prismic.Predicates.at('document.type', this._ref.type),
      Prismic.Predicates.at('document.id', this._ref.id)
    ], { lang: lang === 'en' ? 'en-us' : 'de-de' })

    // console.log(response)

    this.setData(response.results[0].data)
    return this
  }

  @action
  setData (data: any) {
    // needs to be overwitten

  }
}

export class ContentSubTopic extends ContentModel<ContentSubTopic> {
  @observable id: string
  @observable type: string
  // @observable // _data: any
  @observable title: string
  @observable textHtml: string
  @observable urlHeaderImage: string
  @observable headerImageSource: string
  @observable urlTNImage: string
  @observable excerpt: string
  @observable subcontentType: string
  @observable urlVideo: string

  @action
  setData (data: any) {
    // needs to be overwitten
    this.title = data.subcontent_title[0].text
    this.textHtml = PrismicDOM.RichText.asHtml(data.subcontent_text)
    this.urlHeaderImage = data.header_image.url
    this.headerImageSource = ''
    this.urlTNImage = data.thumbnail_image.url
    this.excerpt = data.subcontent_excerpt[0].text
    this.subcontentType = data.subcontent_type
    this.urlVideo = data.subcontent_video_link.url

    for (const source of data.source_image) {
      this.headerImageSource += source.text + ' '
    }
  }

}

interface Constructor<T, Param> {
  new (param: Param): T
}

// add generic for subsection type
export class ContentRef<T extends Fetchable> {
  id: string
  type: string

  // vertiefungType: string

  constructor (data: any, private _constructor: Constructor<T, ContentRef<T>>) {
    this.id = data.id
    this.type = data.type
    // this.title = data.subcontent_title.text
    // // this. = PrismicDOM.RichText.asHtml(data.subcontent_text)
    // this.urlHeaderImage = data.header_image.url
    // // this.urlTNImage = data.
    // this.excerpt = data.subcontent_excerpt.text
    // // this.vertiefungType = data.in_chapter.link_type
  }

  fetchRef (api: PrismicAPI, lang: string): T {
    const obj = new this._constructor(this)

    obj.fetch(api, lang)

    return obj
  }

  // // fetch
  // static async fetchSubContent (api: PrismicAPI, id: string) {
  //   const response = await api.query(
  //     Prismic.Predicates.at('document.id', id), { }
  //   )
  //   console.log(response)

  //   let results = []

  //   for (const document of response.results) {
  //     results.push(new ContentRef(document))
  //   }

  //   // results = _.sortBy(results, 'idx')

  //   return results[0]
  // }
}

export class ContentSection {
  @observable id: string
  @observable type: string
  @observable idx: number
  @observable title: string
  @observable textHtml: string
  @observable urlBGImage: string
  @observable urlBGImageMobile: string
  @observable sourceBGImage: string
  @observable urlBGVideo: string
  @observable _data: any
  @observable subcontentRef: ContentRef<ContentSubTopic>[]

  constructor (id: string, data: any) {
    this._data = data
    this.id = id
    this.type = data.slice_type
    this.idx = data.primary.subchapter_number
    this.title = PrismicDOM.RichText.asText(data.primary.subchapter_title)

    this.textHtml = PrismicDOM.RichText.asHtml(data.primary.running_text)

    this.urlBGImage = data.primary.background_image_subchapter.url
    this.urlBGImageMobile = data.primary.background_image_subchapter.mobile.url
    this.sourceBGImage = PrismicDOM.RichText.asText(data.primary.background_image_subchapter_source)
    this.urlBGVideo = data.primary.background_video_subchapter.url
    this.subcontentRef = []

    for (const subcont of data.items) {
      if (subcont.link_to_subcontent.type === 'subcontent') {
        this.subcontentRef.push(new ContentRef(subcont.link_to_subcontent, ContentSubTopic))
      }
    }
  }
}

export class ContentChapter {
  _data: any
  id: string
  uid: string
  uidAlt?: string
  type: string
  description: string

  idx: number
  title: string
  subline: string
  urlIntroVideo: string
  color: string

  urlOptionalBgImage: string
  sourceOptionalBgImage: string

  urlDesktopMenuThumb: string
  urlMobileMenuThumb?: string

  sections: ContentSection[]

  private constructor (doc: any) {
    this.id = doc.id
    this.uid = doc.uid
    this.uidAlt = doc.alternate_languages[0].uid
    this.type = doc.type
    this._data = doc.data
    const data = this._data

    this.description = data.description

    this.idx = data.chapter_number
    this.title = PrismicDOM.RichText.asText(data.chapter_title)
    this.subline = PrismicDOM.RichText.asText(data.chapter_subline)

    this.urlIntroVideo = data.chapter_intro_video.url
    this.color = data.chapter_color

    this.urlOptionalBgImage = data.optional_background_image ? data.optional_background_image.url : ''
    this.sourceOptionalBgImage = data.optional_background_image_source.length ? data.optional_background_image_source[0].text : ''

    this.urlDesktopMenuThumb = data.desktop_menu_thumbnail.url
    this.urlMobileMenuThumb = data.mobile_menu_thumbnail.url

    this.sections = []

    let i = 0
    if (data.body) {
      for (const section of data.body) {
        this.sections.push(new ContentSection(`${this.uid}_${i++}`, section))
      }
    }
  }

  static async fetchAllChapters (api: PrismicAPI, lang: string) {

    const response = await api.query(
      Prismic.Predicates.at('document.type', 'chapter'), {
        // 'fetch': ['chapter.chapter_number', 'chapter.chapter_title', 'chapter.chapter_subline', 'chapter.chapter_color', 'chapter.chapter_intro_video', 'chapter.optional_background_image', 'chapter.optional_background_image_source']
        // orderings : '[chapter.chapter_number]'
        // lang : lang === 'de' ? 'de-de' : 'en-us'
        lang: lang === 'en' ? 'en-us' : 'de-de'
      }
    )

    // console.log(response)
    let results = []

    for (const document of response.results) {
      results.push(new ContentChapter(document))
    }

    results = _.sortBy(results, 'idx')

    return results

  }

  static async fetchChapter (api: PrismicAPI, id: string) {

    const response = await api.query(
      Prismic.Predicates.at('document.id', id), {
        // 'fetch': ['chapter.chapter_number', 'chapter.chapter_title', 'chapter.chapter_subline', 'chapter.color', 'chapter.chapter_intro_video', 'chapter.optional_background_image', 'chapter.optional_background_image_source']
      }
    )

    console.log('single chapter', response)
    let results = []

    for (const document of response.results) {
      results.push(new ContentChapter(document))
    }

    return results[0]

  }

  @computed
  get icon () {
    if (this.idx > 0) return require(`../common/icons/nr${this.idx}.svg`)
  }
}

export type ContentMenuTextTypes = 'impressum' | 'imprint' | 'data-privacy' | 'privacy-policy'

export class ContentMenuText {
  _data: any
  id: string
  type: string
  title: string
  textHtml: string

  private constructor (doc: any) {
    this.id = doc.id
    this.type = doc.type
    this._data = doc.data.body[0].items[0]
    const data = this._data

    this.title = PrismicDOM.RichText.asText(doc.data.title)
    this.textHtml = PrismicDOM.RichText.asHtml(data.section_running_text)
  }

  static async fetchContentMenuText (api: PrismicAPI, menuContent: ContentMenuTextTypes, lang: string) {

    const response = await api.query(
      Prismic.Predicates.at('document.type', 'impressum-sources-rights'), { lang: lang === 'en' ? 'en-us' : 'de-de' }
    )

    // console.log(response, menuContent)
    let results = []

    for (const document of response.results) {
      if (document.slugs[0] === menuContent) {
        results.push(new ContentMenuText(document))
      }
    }

    return results[0]

  }

  static async fetchDataPrivacy (api: PrismicAPI, lang: string) {

    const response = await api.query(
      Prismic.Predicates.at('document.type', 'impressum-sources-rights'), { lang: lang === 'en' ? 'en-us' : 'de-de' }
    )

    // console.log('impressum', response)
    let results = []

    for (const document of response.results) {
      if (document.slugs[0] === 'data-privacy') {
        results.push(new ContentMenuText(document))
      }
    }

    return results[0]

  }
}

class QuellenSection {
  title: string
  entries: {[key: string]: string}

  constructor (doc: any) {
    this.title = PrismicDOM.RichText.asText(doc.primary.section_title)
    this.entries = {}

    for (const kapitel of doc.items) {
      this.entries[PrismicDOM.RichText.asText(kapitel.section_title)] = PrismicDOM.RichText.asHtml(kapitel.section_running_text)
    }
  }
}

export class ContentQuellen {
  _data: any
  id: string
  type: string
  sections: QuellenSection[]

  private constructor (doc: any) {
    this.id = doc.id
    this.type = doc.type
    this._data = doc.data.body
    const data = this._data

    this.sections = []

    for (const section of data) {
      this.sections.push(new QuellenSection(section))
    }
  }

  static async fetchQuellen (api: PrismicAPI, lang: string) {

    const response = await api.query(
      Prismic.Predicates.at('document.type', 'impressum-sources-rights'), { lang: lang === 'en' ? 'en-us' : 'de-de' }
    )

    // console.log('quellen', response)
    let results = []

    for (const document of response.results) {
      if (document.slugs[0] === 'quellen' || document.slugs[0] === 'sources') {
        results.push(new ContentQuellen(document))
      }
    }

    return results[0]
  }
}

class TimelineSection {
  year: string
  entries: {[key: string]: string}

  constructor (doc: any) {
    this.year = doc.primary.year[0].text
    this.entries = {}

    for (const entry of doc.items) {
      if (entry.date_timeline.length) {
        this.entries[entry.date_timeline[0].text] = entry.description_timeline[0].text
      } else {
        this.entries[''] = entry.description_timeline[0].text
      }
    }
  }
}

export class ContentTimeline {
  _data: any
  id: string
  type: string
  title: string
  sections: TimelineSection[]

  private constructor (doc: any) {
    this.id = doc.id
    this.type = doc.type
    this._data = doc.data
    const data = this._data

    this.title = data.title[0].text
    this.sections = []

    for (const section of data.body) {
      this.sections.push(new TimelineSection(section))
    }
  }

  static async fetchTimeline (api: PrismicAPI, lang: string) {

    const response = await api.query(
      Prismic.Predicates.at('document.type', 'timeline'), { lang: lang === 'en' ? 'en-us' : 'de-de' }
    )

    // console.log('timeline', response)
    let results = []

    for (const document of response.results) {
      results.push(new ContentTimeline(document))
    }

    return results[0]

  }
}

export class ContentTitlePage {
  _data: any
  id: string
  type: string

  title: string
  subtitle: string
  urlBgImage: string

  private constructor (doc: any) {
    this.id = doc.id
    this.type = doc.type
    this._data = doc.data
    const data = this._data

    this.title = data.title[0].text
    this.subtitle = data.subtitle[0].text

    this.urlBgImage = data.background_image.url
  }

  static async fetchTitlePage (api: PrismicAPI, lang: string) {

    const response = await api.query(
      Prismic.Predicates.at('document.type', 'title_page'), { lang: lang === 'en' ? 'en-us' : 'de-de' }
    )

    // console.log('title page', response)
    let results = []

    for (const document of response.results) {
      results.push(new ContentTitlePage(document))
    }

    return results[0]

  }
}

export class StorydocStoreContent {
  @observable titlePage: ContentTitlePage
  @observable timeline: ContentTimeline
  // @observable impressum: ContentImpressum
  chapters = observable.array<ContentChapter>([], { deep: false })
  @observable.shallow subContents: {[key: string]: ContentSubTopic} = {}
  chaps = observable.array<ContentChapter>([], { deep: false })
  // internals
  _prismicApiPromise: PrismicAPIPromise
  _storeCommon: CommonStore

  constructor (commonStore: CommonStore) {
    this._storeCommon = commonStore
    this._prismicApiPromise = Prismic.getApi(
      'https://adenauer-webversion.cdn.prismic.io/api/v2',
      {
        accessToken: 'MC5XNTlrWEI0QUFGNU9xTmdW.bHTvv73vv73vv73vv73vv73vv70K77-9Le-_ve-_vXDvv70d77-9YB3vv73vv70mBwdYfwBS77-977-9SWo'
      }
    )

    this.fetchTitlePage()
    this.fetchTimeline()
    // this.fetchImpressum()
    // this.fetchChapters()
  }

  async getPrismic () {
    return this._prismicApiPromise
  }

  @action
  async fetchTitlePage () {
    const api = await this.getPrismic()

    const titlePage = await ContentTitlePage.fetchTitlePage(api, this._storeCommon.menu.language)

    runInAction(() => {
      this.titlePage = Object.assign({}, titlePage)
    })
  }

  @action
  async fetchTimeline () {
    const api = await this.getPrismic()

    const timeline = await ContentTimeline.fetchTimeline(api, this._storeCommon.menu.language)

    runInAction(() => {
      this.timeline = Object.assign({}, timeline)
    })

    return timeline
  }

  @action
  async fetchContentMenuText (menuContent: ContentMenuTextTypes) {
    const api = await this.getPrismic()

    const impressum = await ContentMenuText.fetchContentMenuText(api, menuContent, this._storeCommon.menu.language)

    // runInAction(() => {
    //   this.impressum = Object.assign({}, impressum)
    // })

    return impressum
  }

  @action
  async fetchQuellen () {
    const api = await this.getPrismic()

    const quellen = await ContentQuellen.fetchQuellen(api, this._storeCommon.menu.language)

    // runInAction(() => {
    //   this.impressum = Object.assign({}, impressum)
    // })

    return quellen
  }

  @action
  async fetchChapters () {
    const api = await this.getPrismic()

    const chapters = await ContentChapter.fetchAllChapters(api, this._storeCommon.menu.language)

    runInAction(() => {
      this.chapters.replace(chapters)
    })
  }

  // @action
  // async getChapter (id: string) {
  //   const api = await this.getPrismic()

  //   if (id in this.chaps) {
  //     return this.chaps[id]
  //   } else {
  //     const chapter = await ContentChapter.fetchChapter(api, id)
  //     this.chaps[chapter.id] = chapter
  //     return chapter
  //   }
  // }

  // getSubContent (ref)
  // looks in array if we have it already then return
  // otherwise create an empty one add to the array and give it back. start fetching for it (in parallel)
  @action
  async getSubContent (ref: ContentRef<ContentSubTopic>) {
    const api = await this.getPrismic()

    if (ref.id in this.subContents) {
      // console.log('ref in subcontents', this.subContents)
      return this.subContents[ref.id]
    } else {
      // console.log('adding ref', this.subContents)
      const subContent = ref.fetchRef(api, this._storeCommon.menu.language)
      this.subContents[ref.id] = subContent
      return subContent
    }
  }
}



// WEBPACK FOOTER //
// ./src/modules/storydoc/storydocStoreContent.tsx